import React, { useState, useMemo } from 'react';
import { Search, Upload, X } from 'lucide-react';
import Dashboard from './components/Dashboard';
import { Patient, Medication, ActionPlan } from './types/patient';
import { mockPatients } from './data/mockData';
import FileUpload from './components/FileUpload';
import johnSmithSuggestions from './data_store/john_smith_suggestions.json';
import johnSmithHistory from './data_store/john_smith_history.json';

function App() {
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [uploadedPatients, setUploadedPatients] = useState<Patient[]>([]);

  // Pre-load Michael Smith's supplemental data
  const enhancedMockPatients = useMemo(() => {
    const johnSmith = mockPatients.find(p => p.id === '1');
    if (johnSmith) {
      // Parse vitals for heart rate and weight
      const heartRate: Array<{ date: string; value: number }> = [];
      const weight: Array<{ date: string; value: number }> = [];

      (johnSmithHistory.vitals || []).forEach((vital: any) => {
        if (vital.measurement === 'Heart rate') {
          const value = parseFloat(vital.value);
          if (!isNaN(value)) {
            heartRate.push({ date: vital.date, value });
          }
        } else if (vital.measurement === 'Weight') {
          const value = parseFloat(vital.value);
          if (!isNaN(value)) {
            weight.push({ date: vital.date, value });
          }
        }
      });

      // Enhance Michael Smith with pre-loaded data
      const enhancedJohnSmith: Patient = {
        ...johnSmith,
        aiSuggestions: johnSmithSuggestions,
        medicalTimeline: johnSmithHistory.timeline.reduce((acc: Record<string, any>, entry: any) => {
          acc[entry.date] = {
            content: entry.event,
            tags: (entry.tags || []).map((tag: string) => ({
              label: tag,
              type: 'category',
              icon: 'Activity',
              color: 'blue'
            }))
          };
          return acc;
        }, {}),
        historicalData: {
          ...johnSmith.historicalData,
          heartRate,
          weight
        },
        procedures: johnSmithHistory.procedures || []
      };
      return [enhancedJohnSmith];
    }
    return mockPatients;
  }, []);

  // Combine enhanced mock patients with uploaded patients
  const allPatients = [...enhancedMockPatients, ...uploadedPatients];

  const handlePatientUploaded = (patient: Patient) => {
    setUploadedPatients(prev => [...prev, patient]);
  };

  const handleMedicationAdded = (patientId: string, medication: Medication) => {
    // Update the selected patient's medications
    if (selectedPatient && selectedPatient.id === patientId) {
      const updatedPatient = {
        ...selectedPatient,
        medications: [...selectedPatient.medications, medication]
      };
      setSelectedPatient(updatedPatient);

      // Also update in uploadedPatients if it's an uploaded patient
      setUploadedPatients(prev =>
        prev.map(p => p.id === patientId ? updatedPatient : p)
      );
    }
  };

  const handlePlanAdded = (patientId: string, plan: ActionPlan) => {
    // Update the selected patient's action plans
    if (selectedPatient && selectedPatient.id === patientId) {
      const updatedPatient = {
        ...selectedPatient,
        actionPlans: [...(selectedPatient.actionPlans || []), plan]
      };
      setSelectedPatient(updatedPatient);

      // Also update in uploadedPatients if it's an uploaded patient
      setUploadedPatients(prev =>
        prev.map(p => p.id === patientId ? updatedPatient : p)
      );
    }
  };

  return (
    <div className="min-h-screen bg-brand-ivory-200">
      <header className="bg-white shadow-sm border-b border-brand-taupe-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Use a 3-column grid so the logo sits centered */}
          <div className="grid grid-cols-3 items-center py-2">
            {/* Left: Title */}
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-xl font-semibold text-brand-dark-500 tracking-tight">Your Health, Your Plan</h1>
              </div>
            </div>

            {/* Center: Logo */}
            <div className="flex justify-center">
              
              <a href="https://forefrontconciergemedicine.com/"
                   target="_blank"
                    rel="noopener noreferrer"
                    >
                  <img
                src={"/logo.png"}
                alt="Clinic / App logo"
                className="h-16 w-auto object-contain"
                aria-hidden={false}
                /> 
              </a>

              
            </div>

            {/* Right: User info */}
            <div className="flex items-center justify-end space-x-4">
              <div className="text-sm text-brand-dark-400">
                Dr. David Johnson
              </div>
              <div className="h-8 w-8 bg-brand-beige-300 rounded-full flex items-center justify-center">
                <span className="text-brand-dark-600 font-medium text-sm">DJ</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {selectedPatient ? (
          <Dashboard
            patient={selectedPatient}
            onBack={() => setSelectedPatient(null)}
            onMedicationAdded={handleMedicationAdded}
            onPlanAdded={handlePlanAdded}
          />
        ) : (
          <PatientSelector
            patients={allPatients}
            onSelectPatient={setSelectedPatient}
            onPatientUploaded={handlePatientUploaded}
          />
        )}
      </main>
    </div>
  );
}

/* PatientSelector component with file upload integration */
interface PatientSelectorProps {
  patients: Patient[];
  onSelectPatient: (patient: Patient) => void;
  onPatientUploaded: (patient: Patient) => void;
}

function PatientSelector({ patients, onSelectPatient, onPatientUploaded }: PatientSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Filter patients based on search query (name or MRN)
  const filteredPatients = patients.filter((patient) => {
    const query = searchQuery.toLowerCase();
    const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
    const mrn = patient.mrn.toLowerCase();

    return fullName.includes(query) || mrn.includes(query);
  });

  const handlePatientUpload = (patient: Patient) => {
    onPatientUploaded(patient);
    setIsUploadModalOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-brand-dark-600 mb-2">Select a Patient</h2>
        <p className="text-brand-dark-400">Choose a patient to view their health dashboard</p>
      </div>

      {/* Search Bar with Upload Button */}
      <div className="max-w-2xl mx-auto">
        <div className="flex gap-3 items-center">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-brand-taupe-500" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or MRN..."
              className="block w-full pl-10 pr-3 py-3 border border-brand-taupe-300 rounded-lg leading-5 bg-white placeholder-brand-taupe-400 focus:outline-none focus:placeholder-brand-taupe-500 focus:ring-2 focus:ring-brand-dark-400 focus:border-brand-dark-500 sm:text-sm"
              aria-label="Search patients by name or MRN"
            />
          </div>
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="flex items-center gap-2 px-4 py-3 bg-brand-dark-500 text-white rounded-lg hover:bg-brand-dark-600 transition-colors duration-200 whitespace-nowrap"
            aria-label="Upload patient file"
          >
            <Upload className="h-5 w-5" />
            <span className="hidden sm:inline">Upload Patient</span>
          </button>
        </div>
      </div>

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setIsUploadModalOpen(false)}
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-brand-taupe-300 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-brand-dark-600">Upload Patient File</h3>
              <button
                onClick={() => setIsUploadModalOpen(false)}
                className="text-brand-taupe-500 hover:text-brand-dark-600 transition-colors"
                aria-label="Close modal"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <FileUpload onPatientUploaded={handlePatientUpload} />
            </div>
          </div>
        </div>
      )}

      {/* Patient Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPatients.length > 0 ? (
          filteredPatients.map((patient) => (
            <div
              key={patient.id}
              onClick={() => onSelectPatient(patient)}
              className="card cursor-pointer hover:shadow-lg transition-shadow"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onSelectPatient(patient);
                }
              }}
              aria-label={`Select patient ${patient.firstName} ${patient.lastName}`}
            >
              <div className="flex items-center space-x-4">
                <div className="h-12 w-12 bg-brand-beige-300 rounded-full flex items-center justify-center">
                  <span className="text-brand-dark-600 font-medium">
                    {patient.firstName[0]}{patient.lastName[0]}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-brand-dark-600">
                    {patient.firstName} {patient.lastName}
                  </h3>
                  <p className="text-brand-dark-400">DOB: {patient.dateOfBirth}</p>
                  <p className="text-sm text-brand-taupe-600">MRN: {patient.mrn}</p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <p className="text-brand-taupe-600">No patients found matching "{searchQuery}"</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;